import React, { Component } from 'react';
import {
    Container, Row, Col, Card, Alert, Button, Badge,
    ListGroup, Modal, Form, Table, InputGroup, Dropdown
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExclamationTriangle, faCheckCircle, faBell,
    faFilter, faSync, faCog, faCheck, faStickyNote
} from '@fortawesome/free-solid-svg-icons';
import { stockService } from '../../../services/StockService';
import { successToastr, errorToastr } from '../../../../Utils/ToastrMessage';

/**
 * Composant de gestion des alertes de stock
 * Couvre les cas d'usage UC-036 à UC-040 pour l'interface utilisateur
 */
class StockAlerts extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            alerts: [],
            lowStockItems: [],
            criticalStockItems: [],

            // Filtres
            filter: 'all', // all, low, critical, acknowledged
            sortBy: 'createdAt',
            sortDirection: 'desc',

            // Modals
            showConfigModal: false,
            showAckModal: false,
            selectedAlert: null,

            // Configuration des seuils
            thresholdConfig: {
                stockId: null,
                minimumThreshold: 0,
                alertThreshold: 0
            },

            // Acquittement avec notes
            acknowledgmentData: {
                note: '',
                acknowledgedBy: 'CURRENT_USER'
            },

            // Statistiques
            alertCounts: {
                total: 0,
                lowStock: 0,
                critical: 0,
                acknowledged: 0
            },

            // Auto-refresh
            refreshInterval: null,
            lastRefresh: null
        };
    }

    async componentDidMount() {
        await this.loadAllAlerts();
        this.setupAutoRefresh();
    }

    componentWillUnmount() {
        if (this.state.refreshInterval) {
            clearInterval(this.state.refreshInterval);
        }
    }

    // Chargement de toutes les données d'alertes
    loadAllAlerts = async () => {
        try {
            this.setState({ loading: true });

            const [alerts, lowStockItems, criticalStockItems] = await Promise.all([
                stockService.getActiveAlerts(),
                stockService.getLowStockItems(),
                stockService.getCriticalStockItems()
            ]);

            const alertCounts = {
                total: alerts.length,
                lowStock: lowStockItems.length,
                critical: criticalStockItems.length,
                acknowledged: alerts.filter(a => a.isAcknowledged).length
            };

            this.setState({
                alerts,
                lowStockItems,
                criticalStockItems,
                alertCounts,
                loading: false,
                lastRefresh: new Date()
            });

        } catch (error) {
            console.error('Error loading alerts:', error);
            this.setState({
                loading: false,
                error: 'Erreur lors du chargement des alertes'
            });
            errorToastr('Erreur lors du chargement des alertes');
        }
    };

    // Configuration du rafraîchissement automatique (30 secondes)
    setupAutoRefresh = () => {
        const refreshInterval = setInterval(() => {
            this.loadAllAlerts();
        }, 30000);

        this.setState({ refreshInterval });
    };

    // UC-039: Génération automatique des alertes
    generateAlerts = async () => {
        try {
            const result = await stockService.generateAlerts();

            successToastr(
                `${result.alertsGenerated} alertes générées (${result.lowStockAlerts} stock faible, ${result.criticalAlerts} critique)`
            );

            // Rafraîchir les données
            await this.loadAllAlerts();

        } catch (error) {
            console.error('Error generating alerts:', error);
            errorToastr('Erreur lors de la génération des alertes');
        }
    };

    // UC-040: Acquittement simple d'une alerte
    acknowledgeAlert = async (alertId) => {
        try {
            await stockService.acknowledgeAlert(alertId);
            successToastr('Alerte acquittée avec succès');
            await this.loadAllAlerts();

        } catch (error) {
            console.error('Error acknowledging alert:', error);
            errorToastr('Erreur lors de l\'acquittement de l\'alerte');
        }
    };

    // UC-040: Acquittement avec notes
    acknowledgeAlertWithNotes = async () => {
        try {
            const { selectedAlert, acknowledgmentData } = this.state;

            await stockService.acknowledgeAlert(selectedAlert.id, {
                note: acknowledgmentData.note,
                acknowledgedBy: acknowledgmentData.acknowledgedBy
            });

            successToastr('Alerte acquittée avec note');

            this.setState({
                showAckModal: false,
                selectedAlert: null,
                acknowledgmentData: { note: '', acknowledgedBy: 'CURRENT_USER' }
            });

            await this.loadAllAlerts();

        } catch (error) {
            console.error('Error acknowledging alert with notes:', error);
            errorToastr('Erreur lors de l\'acquittement');
        }
    };

    // UC-036: Mise à jour des seuils d'alerte
    updateThresholds = async () => {
        try {
            const { thresholdConfig } = this.state;

            await stockService.updateStock(thresholdConfig.stockId, {
                minimumThreshold: thresholdConfig.minimumThreshold,
                alertThreshold: thresholdConfig.alertThreshold,
                updatedBy: 'CURRENT_USER'
            });

            successToastr('Seuils d\'alerte mis à jour');

            this.setState({ showConfigModal: false });
            await this.loadAllAlerts();

        } catch (error) {
            console.error('Error updating thresholds:', error);
            errorToastr('Erreur lors de la mise à jour des seuils');
        }
    };

    // Filtrage et tri des alertes
    getFilteredAndSortedAlerts = () => {
        const { alerts, filter, sortBy, sortDirection } = this.state;

        let filtered = alerts.filter(alert => {
            switch (filter) {
                case 'low':
                    return alert.alertType === 'LOW_STOCK' && !alert.isAcknowledged;
                case 'critical':
                    return alert.alertType === 'CRITICAL_STOCK' && !alert.isAcknowledged;
                case 'acknowledged':
                    return alert.isAcknowledged;
                case 'all':
                default:
                    return true;
            }
        });

        // Tri par priorité puis par date
        filtered.sort((a, b) => {
            // D'abord par priorité (critique > warning > info)
            const priorityOrder = { 'DANGER': 3, 'WARNING': 2, 'INFO': 1 };
            const aPriority = priorityOrder[a.severity] || 0;
            const bPriority = priorityOrder[b.severity] || 0;

            if (aPriority !== bPriority) {
                return bPriority - aPriority; // Ordre décroissant pour priorité
            }

            // Puis par date
            const aDate = new Date(a.createdAt);
            const bDate = new Date(b.createdAt);

            return sortDirection === 'desc' ? bDate - aDate : aDate - bDate;
        });

        return filtered;
    };

    // Rendu d'une alerte individuelle
    renderAlertItem = (alert) => {
        const severity = alert.severity || 'INFO';
        const variant = severity === 'DANGER' ? 'danger' :
                       severity === 'WARNING' ? 'warning' : 'info';

        return (
            <Alert
                key={alert.id}
                variant={variant}
                className={`d-flex justify-content-between align-items-center ${alert.isAcknowledged ? 'opacity-50' : ''}`}
                data-testid={`alert-item-${alert.id}`}
            >
                <div>
                    <div className="d-flex align-items-center mb-1">
                        <FontAwesomeIcon
                            icon={severity === 'DANGER' ? faExclamationTriangle : faBell}
                            className="me-2"
                            data-testid={`icon-${severity === 'DANGER' ? 'exclamation-triangle' : 'bell'}`}
                        />
                        <strong>{alert.productCode}</strong>
                        <Badge
                            bg={variant}
                            className="ms-2"
                            data-testid={`badge-${variant}`}
                        >
                            {severity === 'DANGER' ? 'Rupture' :
                             severity === 'WARNING' ? 'Stock faible' : 'Info'}
                        </Badge>
                    </div>
                    <div>{alert.message}</div>
                    <small className="text-muted">
                        {alert.productName} - Quantité: {alert.currentQuantity} / Seuil: {alert.threshold}
                        <br />
                        Créé le {new Date(alert.createdAt).toLocaleString('fr-FR')}
                    </small>
                </div>

                <div className="d-flex flex-column gap-1">
                    {!alert.isAcknowledged ? (
                        <>
                            <Button
                                size="sm"
                                variant="success"
                                onClick={() => this.acknowledgeAlert(alert.id)}
                                data-testid="button-success"
                            >
                                <FontAwesomeIcon icon={faCheck} className="me-1" />
                                Acquitter
                            </Button>
                            <Button
                                size="sm"
                                variant="info"
                                onClick={() => this.setState({ selectedAlert: alert, showAckModal: true })}
                                data-testid="button-info"
                            >
                                <FontAwesomeIcon icon={faStickyNote} className="me-1" />
                                Avec note
                            </Button>
                        </>
                    ) : (
                        <Badge bg="success">
                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                            Acquittée
                        </Badge>
                    )}
                </div>
            </Alert>
        );
    };

    render() {
        const {
            loading, alerts, alertCounts, filter,
            showConfigModal, showAckModal, selectedAlert,
            thresholdConfig, acknowledgmentData, lastRefresh
        } = this.state;

        const filteredAlerts = this.getFilteredAndSortedAlerts();

        if (loading) {
            return (
                <Container data-testid="container">
                    <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Chargement...</span>
                        </div>
                    </div>
                </Container>
            );
        }

        return (
            <Container data-testid="container">
                {/* En-tête avec statistiques */}
                <Row className="mb-4">
                    <Col>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4>Alertes de Stock</h4>
                                <small className="text-muted">
                                    Dernière mise à jour: {lastRefresh?.toLocaleTimeString('fr-FR')}
                                </small>
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => this.setState({ showConfigModal: true })}
                                    data-testid="button-secondary"
                                >
                                    <FontAwesomeIcon icon={faCog} className="me-1" />
                                    Configuration
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={this.generateAlerts}
                                    data-testid="button-primary"
                                >
                                    <FontAwesomeIcon icon={faSync} className="me-1" />
                                    Générer alertes
                                </Button>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Statistiques des alertes */}
                <Row className="mb-4">
                    <Col xl={3} sm={6}>
                        <Card className="bg-info text-white">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faBell} size="2x" className="me-3" />
                                    <div>
                                        <h4 className="mb-0">{alertCounts.total}</h4>
                                        <span>Total alertes</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={3} sm={6}>
                        <Card className="bg-warning text-white">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="me-3" />
                                    <div>
                                        <h4 className="mb-0">{alertCounts.lowStock}</h4>
                                        <span>Stock faible</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={3} sm={6}>
                        <Card className="bg-danger text-white">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="me-3" />
                                    <div>
                                        <h4 className="mb-0">{alertCounts.critical}</h4>
                                        <span>Critique</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={3} sm={6}>
                        <Card className="bg-success text-white">
                            <Card.Body>
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faCheckCircle} size="2x" className="me-3" />
                                    <div>
                                        <h4 className="mb-0">{alertCounts.acknowledged}</h4>
                                        <span>Acquittées</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Filtres */}
                <Row className="mb-3">
                    <Col>
                        <div className="d-flex gap-2">
                            <Button
                                variant={filter === 'all' ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => this.setState({ filter: 'all' })}
                            >
                                Toutes ({alertCounts.total})
                            </Button>
                            <Button
                                variant={filter === 'critical' ? 'danger' : 'outline-danger'}
                                size="sm"
                                onClick={() => this.setState({ filter: 'critical' })}
                                data-testid="filter-critical-only"
                            >
                                Critique ({alertCounts.critical})
                            </Button>
                            <Button
                                variant={filter === 'low' ? 'warning' : 'outline-warning'}
                                size="sm"
                                onClick={() => this.setState({ filter: 'low' })}
                            >
                                Stock faible ({alertCounts.lowStock})
                            </Button>
                            <Button
                                variant={filter === 'acknowledged' ? 'success' : 'outline-success'}
                                size="sm"
                                onClick={() => this.setState({ filter: 'acknowledged' })}
                            >
                                Acquittées ({alertCounts.acknowledged})
                            </Button>
                        </div>
                    </Col>
                </Row>

                {/* Liste des alertes */}
                <Row>
                    <Col>
                        <Card>
                            <Card.Header>
                                <Card.Title>
                                    Alertes actives ({filteredAlerts.length})
                                </Card.Title>
                            </Card.Header>
                            <Card.Body>
                                {filteredAlerts.length === 0 ? (
                                    <div className="text-center py-4" data-testid="empty-state">
                                        <FontAwesomeIcon
                                            icon={faCheckCircle}
                                            size="3x"
                                            className="text-success mb-3"
                                            data-testid="icon-check-circle"
                                        />
                                        <h5>Aucune alerte active</h5>
                                        <p className="text-muted">
                                            {filter === 'all' ?
                                                'Tous les stocks sont dans les seuils normaux' :
                                                'Aucune alerte dans cette catégorie'
                                            }
                                        </p>
                                    </div>
                                ) : (
                                    <div data-testid="alerts-list">
                                        {/* Section alertes critiques */}
                                        {filteredAlerts.some(a => a.severity === 'DANGER') && (
                                            <div className="mb-4" data-testid="critical-alerts-section">
                                                <h6 className="text-danger">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                                    Alertes Critiques
                                                </h6>
                                                {filteredAlerts
                                                    .filter(a => a.severity === 'DANGER')
                                                    .map(alert => this.renderAlertItem(alert))
                                                }
                                            </div>
                                        )}

                                        {/* Section stock faible */}
                                        {filteredAlerts.some(a => a.severity === 'WARNING') && (
                                            <div className="mb-4" data-testid="low-stock-section">
                                                <h6 className="text-warning">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                                    Stock Faible
                                                </h6>
                                                {filteredAlerts
                                                    .filter(a => a.severity === 'WARNING')
                                                    .map(alert => this.renderAlertItem(alert))
                                                }
                                            </div>
                                        )}

                                        {/* Autres alertes */}
                                        {filteredAlerts.some(a => a.severity === 'INFO') && (
                                            <div>
                                                <h6 className="text-info">
                                                    <FontAwesomeIcon icon={faBell} className="me-2" />
                                                    Informations
                                                </h6>
                                                {filteredAlerts
                                                    .filter(a => a.severity === 'INFO')
                                                    .map(alert => this.renderAlertItem(alert))
                                                }
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Modal de configuration des seuils */}
                <Modal show={showConfigModal} onHide={() => this.setState({ showConfigModal: false })} data-testid="modal">
                    <Modal.Header closeButton>
                        <Modal.Title>Configuration des seuils d'alerte</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form data-testid="form">
                            <Form.Group className="mb-3">
                                <Form.Label>Seuil minimum *</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={thresholdConfig.minimumThreshold}
                                    onChange={(e) => this.setState({
                                        thresholdConfig: {
                                            ...thresholdConfig,
                                            minimumThreshold: parseInt(e.target.value) || 0
                                        }
                                    })}
                                    data-testid="field-minimumThreshold"
                                />
                                <Form.Text className="text-muted">
                                    Déclenche une alerte critique quand le stock atteint ce niveau
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Seuil d'alerte *</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={thresholdConfig.alertThreshold}
                                    onChange={(e) => this.setState({
                                        thresholdConfig: {
                                            ...thresholdConfig,
                                            alertThreshold: parseInt(e.target.value) || 0
                                        }
                                    })}
                                    data-testid="field-alertThreshold"
                                />
                                <Form.Text className="text-muted">
                                    Déclenche une alerte de stock faible quand le stock atteint ce niveau
                                </Form.Text>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.setState({ showConfigModal: false })}>
                            Annuler
                        </Button>
                        <Button variant="primary" onClick={this.updateThresholds} data-testid="button-primary">
                            Sauvegarder
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal d'acquittement avec notes */}
                <Modal show={showAckModal} onHide={() => this.setState({ showAckModal: false })}>
                    <Modal.Header closeButton>
                        <Modal.Title>Acquittement de l'alerte</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedAlert && (
                            <>
                                <Alert variant="info">
                                    <strong>{selectedAlert.productCode}</strong><br />
                                    {selectedAlert.message}
                                </Alert>
                                <Form>
                                    <Form.Group>
                                        <Form.Label>Note d'acquittement</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            placeholder="Expliquez les actions prises ou à prendre..."
                                            value={acknowledgmentData.note}
                                            onChange={(e) => this.setState({
                                                acknowledgmentData: {
                                                    ...acknowledgmentData,
                                                    note: e.target.value
                                                }
                                            })}
                                            data-testid="acknowledgment-note"
                                        />
                                    </Form.Group>
                                </Form>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.setState({ showAckModal: false })}>
                            Annuler
                        </Button>
                        <Button variant="primary" onClick={this.acknowledgeAlertWithNotes} data-testid="button-primary">
                            Acquitter avec note
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        );
    }
}

export default StockAlerts;